import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { KozosCegService } from '../kozos-ceg.service';

// NAPLO közös állapot + API. Az aktív ügyfél + könyvelési év a teljes modulra érvényes,
// és a storage-ben (naploAktiv) perzisztálódik, hogy újratöltés után is megmaradjon.
export interface NaploAktiv {
  intezmeny: string;
  ugyfelId: string | null;
  ugyfelNev: string | null;
  ev: number;
  konyveles_mod: string;   // 'egyszeres' | 'kettos' – ettől függ, melyik könyvelési nézet nyílik
  tipus?: string;          // 'ceg' | 'tarsashaz' – társasháznál nyílik az albetét/közös költség nézet
  /** Használ-e az ügyfél bankot: ettől függ, látszik-e a Bank menü és a bank napló. */
  bank_naplo?: boolean;
}

@Injectable({ providedIn: 'root' })
export class NaploService {
  private readonly base = '/api/naplo';
  aktiv$ = new BehaviorSubject<NaploAktiv>({ intezmeny: '', ugyfelId: null, ugyfelNev: null, ev: new Date().getFullYear(), konyveles_mod: 'egyszeres', tipus: 'ceg' });

  constructor(private http: HttpClient, private data: DataService, private kozos: KozosCegService) {}

  get aktiv(): NaploAktiv { return this.aktiv$.value; }

  async init(): Promise<NaploAktiv> {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    const intezmeny = si?.intezmenyId || '';
    const mentett = (await this.data.getFromStorage('naploAktiv')) || {};
    const a: NaploAktiv = {
      intezmeny,
      ugyfelId: mentett.ugyfelId || null,
      ugyfelNev: mentett.ugyfelNev || null,
      ev: mentett.ev || new Date().getFullYear(),
      konyveles_mod: mentett.konyveles_mod || 'egyszeres',
      tipus: mentett.tipus || 'ceg',
      /*  A bank-kapcsolót IS vissza kell olvasni. Enélkül minden újratöltés (F5, intézmény- vagy
       *  alrendszerváltás) után `undefined` lett, és a Bank menü meg a bank napló eltűnt, pedig
       *  az ügyfélnél be volt kapcsolva – csak az ügyfél újbóli kiválasztása hozta vissza. */
      bank_naplo: !!mentett.bank_naplo,
    };
    this.aktiv$.next(a);
    /*  Modulba lépéskor EGYSZER átvesszük a másik modulban aktív céget. Az init()-en belül, hogy a
     *  keret és a gyerekoldalak (amelyek szintén init()-et hívnak) ugyanazt a céget kapják, ne
     *  töltsön be egyik oldal a régit. A modulból kilépve (layout) a jelző törlődik. */
    if (!this.belepes) this.belepes = this.kozosAtvesz();
    await this.belepes;
    if (this.zaras$.value == null) this.nyitasFrissit();
    return this.aktiv;
  }
  private belepes: Promise<void> | null = null;
  /** A modulból kilépve: a következő belépés újra átveszi a közös céget. */
  kilepes() { this.belepes = null; }

  /*  MODULBA LÉPÉSKOR: ha a másik modulban (Bér, Tárgyi eszköz) másik cég volt aktív, és az itt is
   *  megvan, az legyen az aktív – ne a Könyvelésben legutóbb választott. */
  async kozosAtvesz(): Promise<void> {
    const id = await this.kozos.atveendo('NAPLO');
    const cel = (id && id !== this.aktiv.ugyfelId) ? id : this.aktiv.ugyfelId;
    if (cel && this.aktiv.intezmeny) {
      try {
        const lista: any[] = await this.get('/ugyfelek/' + this.aktiv.intezmeny);
        const u = lista.find((x) => x.id === cel && !x.inaktiv);
        /*  A MEGMARADT ÜGYFÉL ADATAI IS A KISZOLGÁLÓTÓL (2026-11-02, user: „megjegyzi a bank használata
         *  gombot, viszont továbbra se látom a bank főmenüt”). A könyvelési mód, a típus és a „Bank
         *  használata” eddig a böngészőben tárolt másolatból jött: ha az ügyfélnél máshol (más gépen,
         *  más fülön, vagy a pipa javítása előtt) változott, a menü a régi állapotot mutatta, amíg az
         *  ügyfelet újra ki nem választották. Most minden modulba lépéskor frissül; ha nem változott
         *  semmi, nem írunk (a feliratkozók – menü, téma – fölöslegesen ne fussanak újra). */
        const friss = u && { ugyfelId: u.id, ugyfelNev: u.nev, konyveles_mod: u.konyveles_mod || 'egyszeres', tipus: u.tipus || 'ceg', bank_naplo: !!u.bank_naplo };
        const a: any = this.aktiv;
        if (friss && Object.entries(friss).some(([k, v]) => a[k] !== v)) await this.setAktiv(friss);
      } catch { /* marad a legutóbbi */ }
    }
    if (this.aktiv.ugyfelId) await this.kozos.jelol('NAPLO', this.aktiv.ugyfelId);
  }

  async setAktiv(patch: Partial<NaploAktiv>): Promise<void> {
    const elozo = this.aktiv$.value;
    const a = { ...elozo, ...patch };
    this.aktiv$.next(a);
    await this.data.setStorage('naploAktiv', { ugyfelId: a.ugyfelId, ugyfelNev: a.ugyfelNev, ev: a.ev, konyveles_mod: a.konyveles_mod, tipus: a.tipus, bank_naplo: !!a.bank_naplo });
    if (a.ugyfelId) await this.kozos.jelol('NAPLO', a.ugyfelId);
    //  Ügyfél- vagy évváltásnál újra meg kell nézni, meg van-e nyitva az év.
    if (a.ugyfelId !== elozo.ugyfelId || a.ev !== elozo.ev || a.konyveles_mod !== elozo.konyveles_mod) await this.nyitasFrissit();
  }

  /* ══════════ AZ ÉV MEG VAN-E NYITVA (kettős könyvvitel) ══════════
   *
   *  A könyvelés a nyitó egyenlegekkel kezdődik: amíg az év nincs megnyitva, a kivonat, a mérleg
   *  és a naplók féligazságot mutatnának. Ezért a felület LEZÁRJA magát – a főmenü csempéit, a
   *  fejléc menüjét és a könyvelőablak füleit is –, egyedül a nyitás (és az ügyfélváltás) marad.
   *  Az állapot itt él, hogy mindhárom helyen ugyanazt jelentse, és egyszer kelljen lekérni. */
  zaras$ = new BehaviorSubject<any>(null);
  get zarasAllapot(): any { return this.zaras$.value; }
  /** Igaz, ha kettős könyvvitelnél az év MÉG NINCS megnyitva (ilyenkor zárva a felület). */
  get nyitasKell(): boolean {
    const a = this.aktiv;
    return a.konyveles_mod === 'kettos' && !!a.ugyfelId && this.zaras$.value != null && this.zaras$.value.nyitva === false;
  }
  /** Nyitatlan évben is elérhető menüpont/nézet: a nyitás maga és az ügyfélválasztás. */
  nyitasKivetel(ut: string, query?: Record<string, any> | null): boolean {
    const u = String(ut || '');
    if (u.startsWith('/naplo/ugyfelek')) return true;
    if (String(query?.['nezet'] || '') === 'zaras') return true;
    //  Az RLB-import nyitatlan évben is fut: a nyitótételeket is hozhatja (és ezzel megnyitja az évet).
    if (String(query?.['rlbimport'] || '') === '1') return true;
    return false;
  }
  async nyitasFrissit(): Promise<void> {
    const a = this.aktiv;
    if (a.konyveles_mod !== 'kettos' || !a.ugyfelId) { this.zaras$.next(null); return; }
    try { this.zaras$.next(await this.getAbs(`/api/kettos/zaras-allapot/${a.ugyfelId}?ev=${a.ev}`)); }
    catch { this.zaras$.next(null); }
  }

  // ---- vékony HTTP helperek ----
  get<T = any>(path: string): Promise<T> { return firstValueFrom(this.http.get<T>(this.base + path)); }
  post<T = any>(path: string, body: any): Promise<T> { return firstValueFrom(this.http.post<T>(this.base + path, body)); }
  put<T = any>(path: string, body: any): Promise<T> { return firstValueFrom(this.http.put<T>(this.base + path, body)); }
  del<T = any>(path: string): Promise<T> { return firstValueFrom(this.http.delete<T>(this.base + path)); }

  // abszolút útvonalú hívások (más mount-prefix, pl. /api/naplo-eafa)
  getAbs<T = any>(path: string): Promise<T> { return firstValueFrom(this.http.get<T>(path)); }
  postAbs<T = any>(path: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.post<T>(path, body)); }
  putAbs<T = any>(path: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.put<T>(path, body)); }
  delAbs<T = any>(path: string): Promise<T> { return firstValueFrom(this.http.delete<T>(path)); }

  // bináris (pl. PDF) letöltés – az auth-interceptor a tokent így is ráteszi (nem window.open)
  getBlob(path: string): Promise<Blob> { return firstValueFrom(this.http.get(this.base + path, { responseType: 'blob' })); }

  // év query-string segéd
  evq(extra: Record<string, any> = {}): string {
    const p = new URLSearchParams({ ev: String(this.aktiv.ev), ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v != null && v !== '')) });
    return '?' + p.toString();
  }

  /* ===== Háttérfeladatok (hosszú lekérések) =====
     A NAV-lekérések (OSA teljes év, Számlázó szinkron) percekig tartanak; a böngésző addig
     bontja a kapcsolatot, és a felhasználó "sikertelen"-t lát, miközben a szerver dolgozik.
     Ezért a szerveren futnak háttérfeladatként, mi pedig csak az állapotukat kérdezzük.
     Az állapot a SZOLGÁLTATÁSBAN él (nem a komponensben), hogy elnavigálva se vesszen el. */
  feladatok$ = new BehaviorSubject<any[]>([]);
  private pollTimer: any = null;

  get futoFeladatok(): any[] { return this.feladatok$.value.filter((f) => f.allapot === 'fut'); }

  // Elindít egy háttérfeladatot és azonnal visszatér. A hívó nem vár rá – a haladás a sávban látszik.
  async feladatIndit(path: string, body: any = {}): Promise<string | null> {
    const r: any = await this.post(path, body);
    if (r?.feladat_id) { this.feladatFrissit(); this.pollIndit(); }
    return r?.feladat_id || null;
  }

  // Az aktív ügyfél feladatainak lekérése (a sáv ebből rajzol).
  async feladatFrissit(): Promise<void> {
    const ugy = this.aktiv.ugyfelId;
    if (!ugy) { this.feladatok$.next([]); return; }
    try { const r: any = await this.get(`/feladatok/${ugy}`); this.feladatok$.next(r?.feladatok || []); }
    catch { /* a sáv hibája ne törje a lapot */ }
  }

  // Amíg van futó feladat, 2 másodpercenként kérdezünk; utána magától leáll (nincs üres pollozás).
  pollIndit(): void {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(async () => {
      await this.feladatFrissit();
      if (!this.futoFeladatok.length) { clearInterval(this.pollTimer); this.pollTimer = null; }
    }, 2000);
  }

  // Egy konkrét feladat bevárása (az eredményére váró képernyőknek). Elnavigálásnál egyszerűen
  // eldobható – a feladat a szerveren attól még végigfut.
  async feladatVar(id: string, onHalad?: (f: any) => void): Promise<any> {
    for (;;) {
      const f: any = await this.get(`/feladat/${id}`);
      if (onHalad) onHalad(f);
      if (f.allapot === 'kesz') return f;
      if (f.allapot === 'hiba') throw new Error(f.hiba || 'A háttérfeladat hibára futott.');
      if (f.allapot === 'megszakitva') throw new Error(f.uzenet || 'A feladatot megszakították.');
      await new Promise((r) => setTimeout(r, 2000));
    }
  }

  /*  Futó feladat megszakítása. A kiszolgáló azonnal lezárja a sort (így máris indítható új
   *  lekérés), a munkavégző pedig a következő haladás-jelzésnél abbahagyja. */
  async feladatMegszakit(id: string): Promise<void> {
    try { await this.post(`/feladat/${id}/megszakit`, {}); } catch { /* a sáv úgyis frissül */ }
    await this.feladatFrissit();
  }

  async feladatElrejt(id: string): Promise<void> {
    try { await this.del(`/feladat/${id}`); } catch { /* mindegy */ }
    this.feladatFrissit();
  }
}
